/**
 * E-Commerce Analytics & Tracking helper (Google Analytics 4 + Meta / Facebook Pixel).
 *
 * Dynamically initializes scripts when IDs are configured in Shop Settings
 * and tracks standard e-commerce funnel events:
 * - view_item
 * - add_to_cart
 * - begin_checkout
 * - purchase
 */

interface FbqFunction {
  (...args: unknown[]): void
  callMethod?: (...args: unknown[]) => void
  push?: FbqFunction
  loaded?: boolean
  version?: string
  queue?: unknown[][]
}

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
    fbq?: FbqFunction
    dataLayer?: unknown[]
    _fbq?: FbqFunction
  }
}

let initialized = false

export function initAnalytics(settings?: {
  google_analytics_id?: string
  ga4_measurement_id?: string
  meta_pixel_id?: string
}) {
  if (typeof window === 'undefined' || initialized) return
  initialized = true

  const gaId = settings?.google_analytics_id || settings?.ga4_measurement_id

  // 1. Google Analytics 4 (GA4)
  if (gaId && gaId.trim()) {
    const cleanGaId = gaId.trim()
    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${cleanGaId}`
    document.head.appendChild(script)

    window.dataLayer = window.dataLayer || []
    window.gtag = function (...args: unknown[]) {
      window.dataLayer?.push(args)
    }
    window.gtag('js', new Date())
    window.gtag('config', cleanGaId, { send_page_view: true })
  }

  // 2. Meta / Facebook Pixel
  const pixelId = settings?.meta_pixel_id
  if (pixelId && pixelId.trim()) {
    const cleanPixelId = pixelId.trim()
    if (!window.fbq) {
      const fbqFn: FbqFunction = function (this: unknown, ...args: unknown[]) {
        if (fbqFn.callMethod) {
          fbqFn.callMethod(...args)
        } else {
          fbqFn.queue?.push(args)
        }
      }
      fbqFn.push = fbqFn
      fbqFn.loaded = true
      fbqFn.version = '2.0'
      fbqFn.queue = []
      window.fbq = fbqFn
      if (!window._fbq) window._fbq = fbqFn
      const s = document.createElement('script')
      s.async = true
      s.src = 'https://connect.facebook.net/en_US/fbevents.js'
      document.head.appendChild(s)
    }
    window.fbq?.('init', cleanPixelId)
    window.fbq?.('track', 'PageView')
  }
}

export function trackViewItem(item: {
  id: string | number
  name: string
  category?: string
  price: number
}) {
  if (typeof window === 'undefined') return

  if (window.gtag) {
    window.gtag('event', 'view_item', {
      currency: 'UAH',
      value: item.price,
      items: [
        {
          item_id: String(item.id),
          item_name: item.name,
          item_category: item.category,
          price: item.price,
        },
      ],
    })
  }

  if (window.fbq) {
    window.fbq('track', 'ViewContent', {
      content_ids: [String(item.id)],
      content_name: item.name,
      content_type: 'product',
      value: item.price,
      currency: 'UAH',
    })
  }
}

export function trackAddToCart(item: {
  id: string | number
  name: string
  price: number
  quantity?: number
}) {
  if (typeof window === 'undefined') return
  const qty = item.quantity || 1

  if (window.gtag) {
    window.gtag('event', 'add_to_cart', {
      currency: 'UAH',
      value: item.price * qty,
      items: [
        {
          item_id: String(item.id),
          item_name: item.name,
          price: item.price,
          quantity: qty,
        },
      ],
    })
  }

  if (window.fbq) {
    window.fbq('track', 'AddToCart', {
      content_ids: [String(item.id)],
      content_name: item.name,
      content_type: 'product',
      value: item.price * qty,
      currency: 'UAH',
    })
  }
}

export function trackBeginCheckout(totalValue: number) {
  if (typeof window === 'undefined') return

  if (window.gtag) {
    window.gtag('event', 'begin_checkout', {
      currency: 'UAH',
      value: totalValue,
    })
  }

  if (window.fbq) {
    window.fbq('track', 'InitiateCheckout', {
      value: totalValue,
      currency: 'UAH',
    })
  }
}

export function trackPurchase(orderId: string | number, totalValue: number) {
  if (typeof window === 'undefined') return

  if (window.gtag) {
    window.gtag('event', 'purchase', {
      transaction_id: String(orderId),
      currency: 'UAH',
      value: totalValue,
    })
  }

  if (window.fbq) {
    window.fbq('track', 'Purchase', {
      value: totalValue,
      currency: 'UAH',
      content_type: 'product',
    })
  }
}

export function trackSearch(searchTerm: string) {
  if (typeof window === 'undefined' || !searchTerm.trim()) return

  if (window.gtag) {
    window.gtag('event', 'search', {
      search_term: searchTerm.trim(),
    })
  }

  if (window.fbq) {
    window.fbq('track', 'Search', {
      search_string: searchTerm.trim(),
    })
  }
}
